import { useGetNotificationByAcl } from 'api-hooks/notification/query';
import { useGetProductsPurchase } from 'api-hooks/product/order';
import { AuthorizationRules, NavigationRoutes } from 'common/constants';
import { string2money } from 'common/utils/string';
import notification from 'components/common/notification';
import QueryFilter from 'components/common/query-filter/query-filter';
import ListHeader from 'components/elements/list-header';
import { useApplyQueryFilter } from 'hooks/filter/use-apply-query-filter';
import { FilterHandleParentProvider } from 'hooks/filter/use-filter-handle-parent';
import useApplyQuerySort from 'hooks/use-apply-query-sort';
import useComposedQuery from 'hooks/use-composed-query';
import useDate from 'hooks/use-date';
import useGetCrudPermission from 'hooks/use-get-crud-permission';
import useGetListParamsValue from 'hooks/use-get-list-params-value';
import useStatusFilter from 'hooks/use-status-filter';
import { commonColumnTable } from 'legacy-modules/common/column';
import PurchaseListTabs from 'legacy-modules/order/component/purchase-list-tabs';
import PurchasePillStatus from 'legacy-modules/order/component/status';
import { Container, TopContainer } from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';

import ProductOrderTable from './components/table';

export default function ProductOrderList() {
  const { t } = useTranslation();
  const { queryStatus, queryPage, queryTab } = useGetListParamsValue();
  const [page, setPage] = React.useState(queryPage);
  const [activeKey, setActiveKey] = React.useState<React.Key>(queryTab);
  const [status, setStatus] = useStatusFilter<string | undefined>(
    queryStatus,
    setPage,
  );

  const formatDate = useDate();

  const _columns = React.useMemo(
    () => [
      {
        header: t('common:status'),
        accessorKey: 'status',
        minSize: 150,
        cell: ({ getValue }) => (
          <PurchasePillStatus value={getValue() as string} />
        ),
      },
      {
        header: t('document:transaction_at'),
        accessorKey: 'transactionAt',
        minSize: 100,
        sortName: 'transaction_at',
        cell: ({ getValue }) => {
          return <>{formatDate(getValue() as Date, 'dd MMM yyyy')}</>;
        },
      },
      {
        header: t('common:order_number'),
        accessorKey: 'number',
        minSize: 150,
        sortName: 'order_number',
      },
      {
        header: t('common:group_code'),
        accessorKey: 'code',
        minSize: 200,
        sortName: 'code',
      },
      {
        header: t('common:description'),
        accessorKey: 'description',
        minSize: 250,
        sortName: 'description',
      },
      {
        header: t('common:total'),
        accessorKey: 'total',
        minSize: 100,
        sortName: 'total',
        textAlign: 'right',
        cell: ({ getValue }) => {
          return <>{string2money(getValue() as number)}</>;
        },
      },
      {
        header: t('common:agent'),
        accessorKey: 'agent',
        minSize: 200,
      },

      {
        header: t('common:payment_via'),
        accessorKey: 'paymentChannel',
        minSize: 200,
      },
      ...commonColumnTable(t),
    ],
    [formatDate, t],
  );

  const {
    data,
    refetch,
    isLoading,
    isFetching,
    error,
    query,
    extras: [{ filters, setFilters }, { columns }],
  } = useComposedQuery(
    useGetProductsPurchase,
    {
      params: {
        page,
        filter: { status },
      },
    },
    useApplyQueryFilter((data: any) => {
      return data.filters;
    }),
    useApplyQuerySort((data: any) => {
      return data.sorts;
    }, _columns),
  );

  React.useEffect(() => {
    if (!!error && !!error.message) {
      notification.error({ message: error?.message });
    }
  }, [error]);

  const { data: dataNotification, refetch: refetchNotification } =
    useGetNotificationByAcl({
      acl: AuthorizationRules.ProductPurchasesPage,
    });
  const permission = useGetCrudPermission('product-purchases');
  return (
    <FilterHandleParentProvider
      values={{
        filters,
        setFilters,
        page,
        setPage,
      }}
    >
      <Container>
        <ListHeader
          query={query}
          title={t('common:product_purchase')}
          showAdd={permission.create}
          onRefresh={() => {
            refetch();
            refetchNotification();
          }}
          linkAdd={NavigationRoutes.ProductPurchasesCreate}
        />
        <PurchaseListTabs
          {...{
            activeKey,
            setActiveKey: (value) => {
              setActiveKey(value);
              setFilters();
            },
            setStatus,
            badge: dataNotification?.data,
          }}
        />
        <TopContainer>
          <QueryFilter filters={filters} />
        </TopContainer>
        <ProductOrderTable
          productPurchases={data?.data || []}
          loading={isLoading || isFetching}
          page={page}
          onPageChange={(page) => setPage(page)}
          meta={data?.meta}
          sortColumns={columns || []}
        />
      </Container>
    </FilterHandleParentProvider>
  );
}
